import * as THREE from 'three/webgpu';
import { clone } from 'three/addons/utils/SkeletonUtils.js';

export interface TierGround {
  soleSamples: number[];
  idleGroundY: number;
  runGroundY: number;
}

/** 별도 골격에서 측정하므로 전환 중인 실제 mixer의 가중치·시각·보정 상태를 보존한다. */
export function measureTierGround(scene: THREE.Object3D, geometry: THREE.BufferGeometry,
  clips: ReadonlyMap<string, THREE.AnimationClip>): TierGround {
  const copy = clone(scene);
  let mesh: THREE.SkinnedMesh | undefined;
  copy.traverse(o => { if (!mesh && (o as THREE.SkinnedMesh).isSkinnedMesh) mesh = o as THREE.SkinnedMesh; });
  if (!mesh) throw new Error('접지 측정용 메시 없음');
  const body = mesh;
  body.geometry = geometry;
  const ids = geometry.getAttribute('skinIndex'), weights = geometry.getAttribute('skinWeight');
  const feet = new Set(body.skeleton.bones.flatMap((b, i) => /(?:Foot|ToeBase|Toe_End)$/.test(b.name) ? [i] : []));
  const vertices: number[] = [];
  for (let i = 0; i < ids.count; i++) {
    let weight = 0;
    for (let k = 0; k < 4; k++) if (feet.has(ids.getComponent(i, k))) weight += weights.getComponent(i, k);
    if (weight > 0.5) vertices.push(i);
  }
  if (!vertices.length) throw new Error('단계 메시의 발 가중 정점 없음');
  const support = new Set<number>(), point = new THREE.Vector3();
  const mixer = new THREE.AnimationMixer(copy);
  const sample = (name: string, count: number): number => {
    const clip = clips.get(name);
    if (!clip) return 0;
    mixer.stopAllAction();
    const action = mixer.clipAction(clip).reset().setLoop(THREE.LoopOnce, 1).play();
    action.clampWhenFinished = true; action.timeScale = 0;
    let min = Infinity;
    for (let frame = 0; frame <= count; frame++) {
      action.time = clip.duration * frame / count;
      mixer.update(0); copy.updateMatrixWorld(true); body.skeleton.update();
      let low = Infinity, index = -1;
      for (const i of vertices) {
        body.getVertexPosition(i, point).applyMatrix4(body.matrixWorld);
        if (point.y < low) { low = point.y; index = i; }
      }
      if (index >= 0) support.add(index);
      min = Math.min(min, low);
    }
    return Math.max(-0.04, Math.min(0.04, 0.004 - min));
  };
  const idleGroundY = sample('idle', 16);
  const runGroundY = sample('run', Math.max(1, Math.ceil((clips.get('run')?.duration ?? 1) * 120)));
  mixer.stopAllAction(); mixer.uncacheRoot(copy);
  const skeletons = new Set<THREE.Skeleton>();
  copy.traverse(o => { if ((o as THREE.SkinnedMesh).isSkinnedMesh) skeletons.add((o as THREE.SkinnedMesh).skeleton); });
  for (const skeleton of skeletons) skeleton.dispose();
  return { soleSamples: [...support], idleGroundY, runGroundY };
}
